import { ArticleCard } from "./article-card"

export interface FeaturedStoryArticle {
  href: string
  title: string
  excerpt?: string
  image: string
  imageAlt?: string
  authorName?: string | null
  /** FRIDGE-1116 — the author's photo; the byline shows it (the icon is only the fallback). */
  authorImageUrl?: string | null
  readTimeMinutes?: number
  publishedAt?: string | null
  /** Used to choose the inner eyebrow copy. Pass the article slug. */
  slug?: string
}

interface FeaturedStoryCardProps {
  article: FeaturedStoryArticle
  /**
   * Section eyebrow (top-left). Default `"Today's editor's pick"`.
   */
  sectionEyebrow?: string
  /**
   * Optional rotation meta text (top-right). Pass null to hide.
   * Default null.
   */
  rotationMeta?: string | null
  /**
   * Inner eyebrow above the headline. Default derives from `article.slug`:
   * articles starting with `best-` → "Top of category", else → "Read this first".
   */
  innerEyebrow?: string
  /**
   * Top-left corner badge over the image. Default "Featured".
   */
  imageBadge?: string
}

/** Editorial section wrapper; ArticleCard owns all article presentation. */
export function FeaturedStoryCard({
  article,
  sectionEyebrow = "Today’s editor’s pick",
  rotationMeta = null,
  innerEyebrow,
  imageBadge = "Featured",
}: FeaturedStoryCardProps) {
  const eyebrowText =
    innerEyebrow ??
    (article.slug?.startsWith("best-") ? "Top of category" : "Read this first")

  return (
    <section style={{ borderTop: "1px solid #f3f4f6", borderBottom: "1px solid #f3f4f6" }}>
      <div className="ed-container" style={{ paddingTop: 36, paddingBottom: 36 }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: 8,
            flexWrap: "wrap",
            gap: 10,
          }}
        >
          <div className="ed-eyebrow">
            <span className="dot" />
            {sectionEyebrow}
          </div>
          {rotationMeta && (
            <div
              style={{
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                color: "var(--gray-400)",
              }}
            >
              {rotationMeta}
            </div>
          )}
        </div>
        <p className="text-xs font-bold uppercase tracking-wide text-[var(--fridge-blue)] mb-3">
          {eyebrowText}
        </p>
        <ArticleCard
          href={article.href}
          imageUrl={article.image}
          imageAlt={article.imageAlt}
          badge={imageBadge}
          title={article.title}
          excerpt={article.excerpt}
          author={article.authorName ?? undefined}
          authorImageUrl={article.authorImageUrl}
          date={article.publishedAt ?? undefined}
          readTimeMinutes={article.readTimeMinutes}
          className="ed-grid-featured-hero"
        />
      </div>
    </section>
  )
}
